import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError, setAuthToken, clearAuthToken } from "./api";

const AuthContext = createContext({ user: null, loading: true });

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      // Sliding-session: the backend re-issues `access_token` on /me when
      // the current one is past its refresh-window. Persist it so the
      // Bearer fallback (used when cookies are nuked by Safari ITP / PWA
      // standalone mode) also stays fresh.
      if (data?.access_token) setAuthToken(data.access_token);
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password, { remember = false } = {}) => {
    try {
      const { data } = await api.post("/auth/login", { email, password, remember });
      // Persist Bearer token for clients where cookies are unreliable
      // (iOS Safari ITP, PWA standalone, in-app webviews).
      if (data?.access_token) setAuthToken(data.access_token);
      setUser(data);
      // Best-effort: link any anon push subscription on this browser to
      // the now-logged-in user. Never block the auth flow if it fails.
      import("./push").then((m) => m.relinkSubscriptionToCurrentUser?.()).catch(() => {});
      return { ok: true, user: data };
    } catch (e) {
      // Pending invite: backend returns 423 with `{code, message, token}`.
      // Bubble it up so the login page can route the user to the
      // onboarding flow rather than show a generic auth error.
      if (e.response?.status === 423 && e.response?.data?.detail?.code === "invite_pending") {
        return {
          ok: false,
          invitePending: true,
          inviteToken: e.response.data.detail.token || "",
          error: e.response.data.detail.message || "Account setup not finished.",
        };
      }
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (payload) => {
    try {
      // Recruiting-funnel attribution (Feb 27 2026). If the staff
      // view stashed an acquisition source (e.g. "staff_invite") and
      // an inviting owner id in localStorage, fold them into the
      // register payload so the backend can stamp the new user with
      // attribution and bump the inviting owner's referral_count.
      // URL params (`?ref=…&from=…` on /signup) take precedence over
      // localStorage so a freshly-shared link always wins. Both keys
      // are cleared on success.
      const enriched = { ...payload };
      try {
        const params = new URLSearchParams(window.location.search);
        const refFromUrl = params.get("ref");
        const fromInUrl = params.get("from");
        enriched.acquisition_source = (
          enriched.acquisition_source
          || refFromUrl
          || localStorage.getItem("tt_acquisition_source")
          || undefined
        );
        enriched.referrer_owner_id = (
          enriched.referrer_owner_id
          || fromInUrl
          || localStorage.getItem("tt_referrer_owner_id")
          || undefined
        );
      } catch (_unused) { /* private-browsing fallthrough */ }
      const { data } = await api.post("/auth/register", enriched);
      if (data?.access_token) setAuthToken(data.access_token);
      setUser(data);
      // Clear the stashed attribution now that it's claimed — prevents
      // an unrelated future signup on the same device from picking up
      // stale acquisition info.
      try {
        localStorage.removeItem("tt_acquisition_source");
        localStorage.removeItem("tt_referrer_owner_id");
      } catch (_unused2) { /* ignore */ }
      // Same anon-sub-relink hook as login.
      import("./push").then((m) => m.relinkSubscriptionToCurrentUser?.()).catch(() => {});
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // ignore
    }
    clearAuthToken();
    setUser(null);
  };

  // -- Provider logins (Google via Emergent + magic link). Both issue
  // the same JWT cookie shape as `/auth/login`, so the rest of the app
  // doesn't need to know which path the user took. On success we also
  // re-link any anonymous push subscription (same hook as register/login)
  // and migrate the localStorage anonymous-follow list to the account.
  const _afterLogin = async (data) => {
    if (data?.access_token) setAuthToken(data.access_token);
    setUser(data);
    try {
      const m = await import("./push");
      m.relinkSubscriptionToCurrentUser?.();
    } catch {}
    try {
      const f = await import("./follows");
      await f.migrateAnonymousFollowsToAccount?.();
    } catch {}
  };

  const loginWithGoogle = async (sessionId) => {
    try {
      const { data } = await api.post("/auth/google/exchange", { session_id: sessionId });
      await _afterLogin(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const loginWithApple = async (payload) => {
    try {
      const { data } = await api.post("/auth/apple/exchange", payload);
      await _afterLogin(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const requestMagicLink = async (email) => {
    try {
      await api.post("/auth/magic-link/send", { email });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const consumeMagicLink = async (token) => {
    try {
      // Reuse the GoogleExchangeIn shape on the backend (`session_id` field).
      const { data } = await api.post("/auth/magic-link/consume", { session_id: token });
      await _afterLogin(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        logout,
        refresh,
        loginWithGoogle,
        loginWithApple,
        requestMagicLink,
        consumeMagicLink,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
